import { CaretDown, Check } from "@phosphor-icons/react";
import { cn } from "../../design-system/utils/cn";
import { Popover } from "./Popover";

/** One choice in the list. Options sharing a `group` are listed under one
 *  heading, in the order the groups first appear. */
export interface DropdownOption<T extends string> {
  value: T;
  label: string;
  group?: string;
}

export interface DropdownSelectProps<T extends string> {
  /** Field label above the trigger; also names the trigger for assistive tech. */
  label: string;
  value: T;
  options: DropdownOption<T>[];
  onChange: (value: T) => void;
  /** Wrapper classes — usually a width cap (e.g. "max-w-sm"). */
  className?: string;
}

/* A single-select dropdown that reads as a DS form field but renders its own
 * option list, so the choices carry the app's menu styling instead of the
 * browser's native <select> popup (which no design tokens reach).
 *
 * The trigger mirrors the DS Select field; the panel is the same
 * Popover + checked-row menu the workspace switcher and the members table use.
 * Prefer the DS `Select` for long or free-form option lists — this is for a
 * short, curated set where the styling has to match the surrounding surface
 * (e.g. the onboarding wizard). */
export function DropdownSelect<T extends string>({
  label,
  value,
  options,
  onChange,
  className,
}: DropdownSelectProps<T>) {
  const selected = options.find((o) => o.value === value);
  const groups = [...new Set(options.map((o) => o.group))];

  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <span className="text-body-sm font-medium text-fg-1">{label}</span>
      <Popover
        block
        className="w-full"
        trigger={
          <button
            type="button"
            aria-haspopup="listbox"
            aria-label={label}
            className={cn(
              "flex h-9 w-full items-center justify-between gap-2 rounded-md border bg-surface px-2.5",
              "text-left text-body-sm text-fg-1 outline-none",
              "transition-[border-color,box-shadow] duration-fast focus:border-focus focus:shadow-focus",
            )}
          >
            <span className="truncate">{selected?.label ?? value}</span>
            <CaretDown size={14} aria-hidden className="shrink-0 text-fg-2" />
          </button>
        }
      >
        {(close) => (
          <div className="flex flex-col" role="listbox" aria-label={label}>
            {groups.map((group) => (
              <div key={group ?? ""} className="flex flex-col">
                {/* Rendered as authored (not upper-cased like the sidebar's
                    eyebrows) — a heading may name a code identifier, whose
                    casing is meaningful. */}
                {group && (
                  <div className="px-2.5 pb-1 pt-1.5 text-[11px] font-medium tracking-wide text-fg-3">
                    {group}
                  </div>
                )}
                {options
                  .filter((o) => o.group === group)
                  .map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      role="option"
                      aria-selected={option.value === value}
                      onClick={() => {
                        close();
                        onChange(option.value);
                      }}
                      className="flex min-h-9 items-center gap-2.5 rounded-sm-plus px-2.5 py-1.5 text-left text-body-sm text-fg-1 hover:bg-surface-muted"
                    >
                      <span className="min-w-0 flex-1">{option.label}</span>
                      {option.value === value && (
                        <Check className="size-4 shrink-0 text-brand-600" />
                      )}
                    </button>
                  ))}
              </div>
            ))}
          </div>
        )}
      </Popover>
    </div>
  );
}
